'use client';
import styles from './sportstrip.module.scss';
import IconAt from '@/assets/icons/icon-at.svg';
import IconMenuKebab from '@/assets/icons/icon-menu-kebab.svg';
import SXMLink from '@/shared-components/SXMLink';
import cn from 'classnames';
import { useEffect, useId, useRef, useState } from 'react';
import { IconPlayButton } from '@/assets/icons/IconPlayButton';
import { type DynamicTableRow } from '../../DynamicTable/DynamicTable';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import { CSSPropsVars } from '@/interfaces/types/CSSPropsVars';
import { AudioIcon } from '@/ui-components/atoms/AnimatedIcons/AudioIcon/AudioIcon';
import { Text } from '@/ui-components/atoms/Text/Text';
import { LinkWrapperIfHref } from '@/ui-components/atoms/LinkWrapperIfHref/LinkWrapperIfHref';
import { ShowMore } from '@/ui-components/molecules/ShowMore/ShowMore';
import { MoreWaysToListen } from '@/ui-components/molecules/MoreWaysToListen/MoreWaysToListen';

export interface Details {
    logo?: AEMImage;
    color?: string;
    pageUrl?: string;
    playerUrl?: string;
    name: string;
    location: string;
    channel?: string | number;
}

interface SportStripProps {
    homeTeam?: Details;
    awayTeam?: Details;
    league?: Details;
    moreWays?: DynamicTableRow[];
    isLive: boolean;
    subStrip?: React.ReactNode;
    tabletContentLayout?: 'row' | 'column';
    ariaLabel?: string;
    soccerStrip?: boolean;
}

type TeamType = 'Away' | 'Home' | 'League';

export function buildListenAriaLabel(name: string, channel?: string | number): string {
    return `Listen to ${name}${channel ? ` on ${channel}` : ''}`;
}

function TeamLogo({ logo }: { logo?: AEMImage }): React.JSX.Element | null {
    if (!logo) return null;
    return (
        <ImageWrapper
            src={logo.url}
            alt={logo.altText}
            optimizedWidth={logo.width}
            optimizedHeight={logo.height}
            maxWidth={90}
        />
    );
}

interface ListenLiveProps {
    buttonLabel: string;
    eventName: string;
    playerUrl?: string;
    isLive: boolean;
}

function ListenLive({ buttonLabel, eventName, playerUrl, isLive }: ListenLiveProps): React.JSX.Element | null {
    if (!isLive || !playerUrl) return null;

    return (
        <SXMLink
            href={playerUrl}
            target="_blank"
            aria-label={`Listen Live to ${eventName}`}
            className={styles.listenLive}
        >
            <div className={styles.listenLiveLabel}>{buttonLabel}</div>
            <div className={styles.listenLiveIcon}>
                <IconPlayButton />
            </div>
        </SXMLink>
    );
}

interface TeamDetailsProps {
    type: TeamType;
    team: Details;
    isLive: boolean;
    league?: Details;
    isSingleTeam: boolean;
}

function TeamDetails({ type, team, isLive, league, isSingleTeam }: TeamDetailsProps): React.JSX.Element {
    const isLeague = type === 'League';

    const detailsClasses = cn(styles.details, styles.contentSection, {
        [styles.fullWidth]: isLeague,
        [styles.isLive]: isLive,
        [styles.singleTeam]: isSingleTeam,
    });
    const detailsCustomProperties = {
        '--sportsStripTeamColor': team.color,
    } as CSSPropsVars;

    return (
        <div className={detailsClasses} style={detailsCustomProperties}>
            {(!isLeague || team.logo) && (
                <div className={isLeague ? styles.leagueLogo : styles.teamLogo}>
                    <TeamLogo logo={team.logo} />
                </div>
            )}
            <div className={styles.detailsBody}>
                <div className={styles.contentSection}>
                    <LinkWrapperIfHref
                        link={{
                            href: team.pageUrl,
                            target: '_blank',
                            'aria-label': buildListenAriaLabel(team.name, team.channel),
                        }}
                        className={styles.teamNameLink}
                    >
                        <Text tag="div" variant="text-bold" className={styles.teamName}>
                            {team.name}
                        </Text>
                    </LinkWrapperIfHref>
                    {!isLeague && (
                        <Text tag="div" variant="text-small" className={styles.eyebrowLink}>
                            {type}{' '}
                            {team.channel && (
                                <>
                                    &bull; {!isNaN(parseInt(`${team.channel}`)) ? 'CH' : ''} {team.channel}
                                </>
                            )}
                            <ListenLive
                                buttonLabel=""
                                eventName={team.name}
                                playerUrl={team.playerUrl}
                                isLive={isLive}
                            />
                        </Text>
                    )}
                    {isLeague && (
                        <Text tag="div" variant="text-small" className={styles.locationText}>
                            {team.location}
                        </Text>
                    )}
                </div>

                {isLeague && league && (
                    <div className={styles.leagueDetails}>
                        <LinkWrapperIfHref
                            link={{
                                href: league.playerUrl,
                                target: '_blank',
                                'aria-label': buildListenAriaLabel(league.name, league.channel),
                            }}
                        >
                            <div className={styles.channel}>
                                <Text tag="div" variant="text-small">
                                    {!isNaN(parseInt(`${league.channel}`)) ? 'CH' : ''} {league.channel}
                                </Text>
                            </div>
                        </LinkWrapperIfHref>
                        <ListenLive
                            buttonLabel="Listen Live"
                            eventName={league.name}
                            playerUrl={league.playerUrl}
                            isLive={isLive}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default function SportStrip(props: SportStripProps): React.JSX.Element {
    const contentId = useId();

    const [showContent, setShowContent] = useState(false);

    const stripRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handlePointerDown = (event: PointerEvent) => {
            if (!stripRef.current) return;

            if (!stripRef.current.contains(event.target as Node)) {
                setShowContent(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setShowContent(false);
            }
        };

        document.addEventListener('pointerdown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const onPointerLeave = (e: React.PointerEvent) => {
        if (e.pointerType !== 'mouse') return;
        setShowContent(false);
    };

    const contentStyles = cn(styles.content, {
        [styles.tabletColumnLayout]: props.tabletContentLayout === 'column',
        [styles.league]: props.league,
        [styles.isLive]: props.isLive,
    });

    const stripStyles = cn(styles.strip, {
        [styles.hasSubStrip]: props.subStrip,
        [styles.hoverStyles]:
            props.league?.pageUrl || props.homeTeam?.pageUrl || props.awayTeam?.pageUrl || props.isLive,
    });

    const [firstTeam, secondTeam] = props.soccerStrip
        ? ([
              { type: 'Home', team: props.homeTeam },
              { type: 'Away', team: props.awayTeam },
          ] as const)
        : ([
              { type: 'Away', team: props.awayTeam },
              { type: 'Home', team: props.homeTeam },
          ] as const);

    const separator = props.soccerStrip ? (
        <div className={styles.at}>
            <span>vs</span>
        </div>
    ) : (
        <div className={styles.at}>
            <IconAt fill="var(--base-contrast-subtle)" aria-hidden={true} />
        </div>
    );

    const isSingleTeam = !props.homeTeam;

    return (
        <div className={styles.focusContainer} data-cy="molecules-sports-strip">
            <div className={stripStyles} onPointerLeave={onPointerLeave} ref={stripRef}>
                <div className={styles.container}>
                    <div className={contentStyles}>
                        {firstTeam.team && (
                            <TeamDetails
                                type={firstTeam.type}
                                team={firstTeam.team}
                                isLive={props.isLive}
                                isSingleTeam={isSingleTeam}
                            />
                        )}
                        {secondTeam.team && (
                            <>
                                {separator}
                                <TeamDetails
                                    type={secondTeam.type}
                                    team={secondTeam.team}
                                    isLive={props.isLive}
                                    isSingleTeam={isSingleTeam}
                                />
                            </>
                        )}
                        {props.league && (
                            <TeamDetails
                                type="League"
                                team={props.league}
                                isLive={props.isLive}
                                league={props.league}
                                isSingleTeam={isSingleTeam}
                            />
                        )}
                    </div>

                    {props.moreWays && (
                        <div className={styles.menu}>
                            {props.isLive && (
                                <div className={styles.audioIcon}>
                                    <AudioIcon />
                                </div>
                            )}

                            <ShowMore
                                className={styles.moreInfo}
                                buttonClassName={styles.moreInfoButton}
                                arrowClassName={styles.showMoreArrow}
                                showText={<Kebab />}
                                hideText={<Kebab />}
                                ariaLabel={props.ariaLabel}
                                activeToggleTextPosition="top"
                                id={contentId}
                                shownClassName={styles.moreInfoActive}
                                contentClassName={styles.moreInfoContent}
                                onClickHandler={() => setShowContent(true)}
                                isContentShown={showContent}
                                ariaHaspopup
                            >
                                <MoreWaysToListen
                                    variant="sports-strip"
                                    rows={props.moreWays}
                                    popoverClassName={styles.popOver}
                                />
                            </ShowMore>
                        </div>
                    )}
                </div>
                {props.subStrip}
            </div>
        </div>
    );
}

function Kebab() {
    return (
        <div className={styles.kebab}>
            <IconMenuKebab />
        </div>
    );
}
